import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../locales/translations';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { AVATARS } from '../assets/index';

const { FiChevronLeft, FiChevronRight, FiStar } = FiIcons;

const ITEMS_PER_PAGE = 4;
const AUTO_INTERVAL = 4500;

// Preload avatars for a given page to eliminate lag on navigation
const preloadAvatars = (pageIndex) => {
  const start = pageIndex * ITEMS_PER_PAGE;
  for (let i = 0; i < ITEMS_PER_PAGE; i++) {
    const src = AVATARS[(start + i) % AVATARS.length];
    const img = new Image();
    img.src = src;
  }
};

const AvatarImage = memo(({ src, name, initials }) => {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm shrink-0 border-2 border-orange-500/30">
        {initials}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      loading="lazy"
      decoding="async"
      width={40}
      height={40}
      className="w-10 h-10 rounded-full object-cover shrink-0 border-2 border-orange-500/30"
      onError={() => setFailed(true)}
    />
  );
});

const TestimonialCard = memo(({ item, idx, avatarUrl }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -24 }}
    transition={{ duration: 0.45, delay: idx * 0.07 }}
    className="bg-[#1a1a24] p-6 rounded-2xl border border-white/10 hover:border-orange-500/30 transition-all flex flex-col justify-between h-full"
  >
    <div>
      <div className="flex text-orange-400 mb-4">
        {[1, 2, 3, 4, 5].map((star) => (
          <SafeIcon key={star} icon={FiStar} className="fill-current text-sm" />
        ))}
      </div>
      <p className="text-gray-300 text-sm sm:text-base leading-relaxed italic mb-6">
        {item.quote}
      </p>
    </div>
    <div className="flex items-center gap-3">
      <AvatarImage
        src={avatarUrl}
        name={item.name}
        initials={item.initials}
      />
      <div>
        <h4 className="text-white font-bold text-sm">{item.name}</h4>
        <p className="text-orange-400 text-xs">{item.role}</p>
      </div>
    </div>
  </motion.div>
));

const Testimonials = () => {
  const { language } = useLanguage();
  const text = t[language].testimonials;
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);
  const sectionRef = useRef(null);
  const inViewRef = useRef(false);

  const totalPages = Math.ceil(text.items.length / ITEMS_PER_PAGE);

  const next = useCallback(() => {
    setPage((p) => {
      const nextPage = (p + 1) % totalPages;
      // Preload the page after next
      preloadAvatars((nextPage + 1) % totalPages);
      return nextPage;
    });
  }, [totalPages]);

  const prev = useCallback(() => {
    setPage((p) => {
      const prevPage = (p - 1 + totalPages) % totalPages;
      preloadAvatars((prevPage - 1 + totalPages) % totalPages);
      return prevPage;
    });
  }, [totalPages]);

  // Reset page on language change
  useEffect(() => { setPage(0); }, [language]);

  // Preload first two pages on mount
  useEffect(() => {
    preloadAvatars(0);
    preloadAvatars(1);
  }, []);

  // IntersectionObserver — only autoplay when section is visible
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => { inViewRef.current = entry.isIntersecting; },
      { threshold: 0.2 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  // Autoplay — respects pause and visibility
  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      if (inViewRef.current) next();
    }, AUTO_INTERVAL);
    return () => clearInterval(timer);
  }, [paused, next]);

  const visible = text.items.slice(
    page * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE + ITEMS_PER_PAGE
  );

  return (
    <section
      id="testimonials"
      ref={sectionRef}
      className="py-16 sm:py-24 bg-[#0a0a0f] relative border-t border-white/5 overflow-hidden"
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-orange-500/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-4"
          >
            {text.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-base sm:text-xl text-gray-400"
          >
            {text.subtitle}
          </motion.p>
        </div>

        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={`${language}-${page}`}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
            >
              {visible.map((item, idx) => (
                <TestimonialCard
                  key={`${item.name}-${page}`}
                  item={item}
                  idx={idx}
                  avatarUrl={AVATARS[(page * ITEMS_PER_PAGE + idx) % AVATARS.length]}
                />
              ))}
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center justify-center gap-4 mt-10">
            <button
              onClick={prev}
              className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-orange-500/20 hover:border-orange-500/40 transition-all"
            >
              <SafeIcon icon={FiChevronLeft} className="text-base" />
            </button>

            <div className="flex gap-2">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === page
                      ? 'w-6 bg-gradient-to-r from-orange-500 to-purple-500'
                      : 'w-2 bg-white/20 hover:bg-white/40'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={next}
              className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-orange-500/20 hover:border-orange-500/40 transition-all"
            >
              <SafeIcon icon={FiChevronRight} className="text-base" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;